import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';
import { reviewerHandoverStep } from './reviewStage';

interface MarkStepCompleteButtonProps {
  cycle: ReviewCycle;
  // Which step this user acts as (medical / legal / producer). Comes from
  // useCurrentUserStep in the parent chain. Null when the caller has no
  // meaningful step.
  currentUserStep: 'medical' | 'legal' | 'producer' | null;
  // Used to tell whether this reviewer has already submitted their risk
  // assessment — the step cannot be completed without one.
  userEmail?: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onComplete: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const TERMINAL = new Set(['approved', 'rejected', 'canceled', 'superseded']);
const STEP_LABEL: Record<string, string> = {
  medical: 'medical review',
  legal: 'legal review'
};

/**
 * Phase 12E — "Mark my step complete" action for a sequenced reviewer.
 *
 * Renders when:
 *   - user has a currentUserStep,
 *   - the cycle is non-terminal,
 *   - the user's step is the NEXT uncompleted step in the cycle's
 *     reviewSequence (per D64 sequencing — earlier steps must finish
 *     before later ones can act).
 *
 * For step='producer' the button reads "Mark ready to distribute" per
 * D64/D65 (Producer step 3 is the final commercial polish gate).
 *
 * Clicking POSTs to /cycles/:id/steps/:step/complete which:
 *   - appends stepCompletions entry
 * The parent's onComplete() re-fetches the cycle so the row moves out
 * of "Action Required" on the producer dashboard.
 */
const MarkStepCompleteButton: React.FC<MarkStepCompleteButtonProps> = ({
  cycle, currentUserStep, userEmail, getAuthHeaders, onComplete
}) => {
  // Removed 2026-08-21 (Fredrik): a "Final risk" dropdown used to sit next to
  // this button and write a SECOND risk level into reviewerAssessments[].
  //
  // It was a fallback from before "completing your review IS the handover"
  // (2026-08-20) — back then a reviewer could complete a step having never
  // filled the assessment, and the DM would see no risk from them. The
  // assessment form now completes the step itself, so the fallback duplicated
  // the one place risk belongs. Worse, it was prefilled from the reviewer's
  // last assessment while the backend only fills in a STUB entry, so a
  // reviewer who had already submitted got a second entry appended with an
  // empty summary — the DM saw them twice, once blank. The safety net it was
  // reaching for is now a guard: no submitted assessment, no step-complete.
  const submittedAssessment = userEmail
    ? (cycle.reviewerAssessments || []).find(a =>
        a.reviewer?.email?.toLowerCase() === userEmail.toLowerCase() && a.submittedAt)
    : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (TERMINAL.has(cycle.status)) return null;

  // 2026-08-31 — "the NEXT step, if it is yours", via the shared rule, which
  // is where the producer step is excluded. This used to be a raw
  // `nextStep.step !== currentUserStep` comparison, and useCurrentUserStep
  // resolves a decision maker or admin WITHOUT a ReviewerContext to 'producer'
  // as an edit-lock fallback — so every admin viewing a cycle parked on the
  // producer step got the producer's own green button, beside "Complete
  // Review", with nothing on the page saying it completed a named colleague's
  // step. Walk fix F11 had already fixed exactly this for the sequence
  // indicator by routing through isYourStep; this component never got it.
  //
  // A decision maker who genuinely needs to complete that step for someone
  // does it through ProducerReadbackPanel's on-behalf path, which names the
  // person. The producer step has one home.
  const step = reviewerHandoverStep(cycle, currentUserStep, userEmail);
  if (!step) return null;

  const label = `Mark ${STEP_LABEL[step] || 'step'} complete`;
  const tooltip = `Mark your ${STEP_LABEL[step]} step complete. Passes the sequence to the next reviewer.`;

  // Hidden in the normal flow (Fredrik, 2026-08-21 — "maybe not need the mark
  // medical review complete").
  //
  // Since 2026-08-20 "Complete Review" submits the assessment AND completes the
  // step, and since today a medical/legal step cannot be completed without that
  // assessment. So wherever this button rendered, Complete Review would have
  // done the same thing — it was a second button for an act the reviewer had
  // already been given a better path to.
  //
  // One case genuinely survives: a reviewer who submitted their assessment
  // OUT OF TURN. The auto-complete is gated on their step being next, so the
  // step stays open, and when it does come round the assessment form is
  // already in its completed state. That reviewer has no other way to hand
  // over — so the button appears exactly there and nowhere else.
  //
  // Producer is untouched: "ready to distribute" is a workflow signal with no
  // assessment behind it, so the button is that step's only control.
  // Every step reaching here is medical or legal — the producer step is
  // excluded by reviewerHandoverStep — so the assessment guard is unconditional.
  if (!submittedAssessment) return null;

  async function onClick() {
    if (!window.confirm(`${label}?\n\n${tooltip}`)) return;
    setBusy(true); setError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/steps/${step}/complete`,
        {},
        { headers }
      );
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to mark step complete');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
      <button
        className="review-btn"
        onClick={onClick}
        disabled={busy}
        style={{
          background: '#123250',
          color: '#fff',
          border: 'none',
          padding: '6px 14px',
          borderRadius: 4,
          cursor: busy ? 'wait' : 'pointer',
          fontSize: 13,
          fontWeight: 600
        }}
        title={tooltip}
        data-testid={`mark-step-complete-${step}`}
      >
        {busy ? 'Marking...' : label}
      </button>
      {error && <span style={{ color: '#dc3545', fontSize: 12, marginLeft: 8 }}>{error}</span>}
    </div>
  );
};

export default MarkStepCompleteButton;
